import { strToU8, zipSync } from "fflate";
import { createMinecraftExport } from "../export/minecraftExport.ts";
import { editorAi, importAiProject, markAiChanged, useEditorStore } from "../store/editorStore.ts";
import { createReviewFiles } from "./reviewBundle.ts";
import { currentCatalog } from "./workspace.ts";
import type { Bounds, Position, ReviewFinding } from "./workspace.ts";
import type { CameraView, ExportFormat } from "../types/editor.ts";

export function downloadFile(filename: string, contents: string | Uint8Array, mime = "application/json") {
  const blob = new Blob([typeof contents === "string" ? contents : new Uint8Array(contents)], {type:mime});
  const url = URL.createObjectURL(blob), anchor = document.createElement("a");
  anchor.href = url; anchor.download = filename; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function captureView(view: CameraView = "perspective") {
  const renderer = useEditorStore.getState().renderer;
  if (!renderer) throw new Error("The viewport is not ready.");
  const sceneRevision = editorAi.revision;
  const image = await renderer.captureView(view);
  if (editorAi.revision !== sceneRevision) throw new Error("Scene changed during capture. Retry for a consistent result.");
  return { sceneRevision, coordinateSystem: editorAi.getProject().coordinateSystem, ...image };
}

export async function downloadReviewBundle() {
  const revision = editorAi.revision;
  const files = createReviewFiles(editorAi);
  const cameras: unknown[] = [];
  if (useEditorStore.getState().renderer) {
    for (const view of ["perspective", "top", "front"] as const) {
      const image = await captureView(view);
      const { dataUrl, ...metadata } = image;
      const bytes = Uint8Array.from(atob(dataUrl.split(",")[1]), character => character.charCodeAt(0));
      files["renders/" + view + ".png"] = bytes;
      cameras.push(metadata);
    }
    files["renders/cameras.json"] = strToU8(JSON.stringify(cameras, null, 2));
  }
  if (editorAi.revision !== revision) throw new Error("Scene changed while preparing the bundle. Retry.");
  downloadFile("voxel-studio-review.zip", zipSync(files), "application/zip");
  return {sceneRevision:revision,files:Object.keys(files)};
}

export function createBrowserApi() {
  return {
    version: 1,
    capabilities: ["state", "inspect", "project-import-export", "catalog", "preview", "revision-checked-edit", "review", "focus", "capture", "undo-redo"],
    getContract: () => ({
      apiVersion:1,project:{format:"minecraft-voxel-studio",version:1},review:{format:"minecraft-voxel-review",version:1},
      build:{format:"minecraft-design-protocol",version:1},coordinateSystem:editorAi.getProject().coordinateSystem,
      limits:{projectBlocks:null,operations:5000,nestingDepth:32,voxelWorkPerBuild:1000000,statePage:5000,inspectionRadius:8},
      workflow:["getState","getCatalog","preview","applyBuild with expectedRevision","inspect","downloadReviewBundle","importReviews","focusReview","setReviewStatus"],
      documentation:"docs/ai-workflow.md",schemas:"docs/schemas"
    }),
    getState: (options?: {bounds?:Bounds;offset?:number;limit?:number}) => editorAi.snapshot(options),
    getStatus: () => {
      const state = useEditorStore.getState();
      return {projectVersionId:state.projectVersionId,versionStatus:state.versionStatus,assets:state.textureStatus,
        viewportReady:Boolean(state.renderer),sceneRevision:editorAi.revision};
    },
    inspect: (position:Position,radius=1) => editorAi.inspect(position,radius),
    getProject: () => editorAi.getProject(),
    getCatalog: (options: {query?:string;offset?:number;limit?:number} = {}) => {
      const offset = options.offset ?? 0, limit = options.limit ?? 200;
      if (!Number.isSafeInteger(offset) || offset < 0 || !Number.isSafeInteger(limit) || limit < 1 || limit > 5000) throw new Error("Invalid catalog pagination.");
      const catalog = currentCatalog().filter(item => item.id.includes(options.query ?? ""));
      return {source:editorAi.catalogSource,minecraftVersion:editorAi.world.minecraftVersion,total:catalog.length,offset,limit,
        hasMore:offset+limit<catalog.length,blocks:catalog.slice(offset,offset+limit)};
    },
    importProject: async (document:unknown, options: {expectedRevision?:string;loadOfficialAssets?:boolean} = {}) => {
      if (editorAi.world.blocks.size && options.expectedRevision !== editorAi.revision) throw new Error("Replacing a nonempty project requires the current expectedRevision.");
      return importAiProject(document, options.loadOfficialAssets !== false);
    },
    setIntent: (name:string,brief:string) => {editorAi.setIntent(name,brief);markAiChanged();return {sceneRevision:editorAi.revision};},
    addRegion: (region:unknown) => {const result=editorAi.addRegion(region);markAiChanged();return result;},
    preview: (protocol:unknown,expectedRevision?:string) => editorAi.preview(protocol,expectedRevision),
    applyBuild: (request: {protocol:unknown;expectedRevision:string;author?:string}) => {
      const receipt=editorAi.apply(request.protocol,request.expectedRevision,request.author);
      markAiChanged(); return receipt;
    },
    analyze: () => editorAi.analyze(),
    importReviews: (document:unknown) => {const result=editorAi.importReviews(document);markAiChanged();return result;},
    setReviewStatus: (id:string,status:ReviewFinding["status"]) => {const result=editorAi.setReviewStatus(id,status);markAiChanged();return result;},
    focusReview: (id:string) => {
      const finding=editorAi.reviews.find(item=>item.id===id);
      if (!finding) throw new Error("Review finding does not exist.");
      const store=useEditorStore.getState();
      store.setSelectedPosition(finding.bounds.min);store.setSelectionEnd(finding.bounds.max);
      store.renderer?.focusBounds(finding.bounds);
      return {bounds:finding.bounds,stale:finding.sceneRevision!==editorAi.revision};
    },
    captureView,
    downloadReviewBundle,
    export: (format:ExportFormat="json") => {
      if (!["json","mcfunction","commands","skript"].includes(format)) throw new Error("Unknown export format.");
      return format==="json" ? editorAi.getProject() : createMinecraftExport(editorAi.world,format,editorAi.origin);
    },
    undo: () => {const changed=editorAi.world.undo();markAiChanged();return {changed,sceneRevision:editorAi.revision};},
    redo: () => {const changed=editorAi.world.redo();markAiChanged();return {changed,sceneRevision:editorAi.revision};}
  };
}

export type MinecraftDesignerApi = ReturnType<typeof createBrowserApi>;
declare global { interface Window { minecraftDesignerAPI: MinecraftDesignerApi } }
export function installBrowserApi() {
  window.minecraftDesignerAPI = createBrowserApi();
}
